import { IconChevronLeft16 } from '@warp-ds/icons/react/chevron-left-16';
import { IconChevronRight16 } from '@warp-ds/icons/react/chevron-right-16';
import { IconImage24 } from '@warp-ds/icons/react/image-24';
import cn from 'classnames';
import { type CSSProperties, memo } from 'react';
import { useCarouselDots } from './hooks/useCarouselDots';
import { useCarouselNavigation } from './hooks/useCarouselNavigation';
import { useImageLoading } from './hooks/useImageLoading';
import styles from './ImageCarousel.module.css';
import { DOT_SIZE_REM, DOT_SPACING_REM } from './utils/constants';
import type { CarouselStyles, ImageCarouselProps } from './utils/types';

export const ImageCarousel = memo(({ images, alt, className, onImageChange, initialIndex = 0, loading = 'lazy' }: ImageCarouselProps) => {
    const { displayableImages, handleImageLoadError } = useImageLoading(images);
    const totalImages = displayableImages.length;

    const { activeImageIndex, showControls, showPrevButton, showNextButton, handleNavigationClickPrev, handleNavigationClickNext } =
        useCarouselNavigation({
            initialIndex,
            totalImages,
            onImageChange,
        });

    const { dotsTrackOffsetRem, edgeDotIndices } = useCarouselDots({
        totalImages,
        activeImageIndex,
    });

    if (!totalImages) {
        return (
            <div
                className={cn('relative w-full h-full overflow-hidden s-bg-subtle', styles.carousel, className)}
                data-testid="image-carousel">
                <div className="flex items-center justify-center w-full h-full s-text-subtle">
                    <IconImage24 aria-hidden />
                </div>
            </div>
        );
    }

    const carouselStyles: CarouselStyles & CSSProperties = {
        '--dot-size': `${DOT_SIZE_REM}rem`,
        '--dot-spacing': `${DOT_SPACING_REM}rem`,
    };

    return (
        <div
            className={cn('relative w-full h-full overflow-hidden s-bg-subtle', styles.carousel, className)}
            style={carouselStyles}
            data-testid="image-carousel">
            <div
                className={cn('flex w-full h-full', styles.imageTrack)}
                style={{ transform: `translateX(-${activeImageIndex * 100}%)` }}
                data-testid="image-track">
                {displayableImages.map(({ image, originalIndex }, index) => (
                    <img
                        key={originalIndex}
                        src={image}
                        alt={`${alt ?? ''} ${index + 1} av ${totalImages}`}
                        className="w-full h-full shrink-0 object-cover"
                        loading={index === activeImageIndex ? loading : 'lazy'}
                        onError={() => handleImageLoadError(originalIndex)}
                    />
                ))}
            </div>
            {showControls && (
                <>
                    {showPrevButton && (
                        <button
                            type="button"
                            onClick={handleNavigationClickPrev}
                            className={cn(
                                'flex items-center justify-center absolute border-none rounded-full z-10 md:opacity-0 pr-2',
                                styles.navButton,
                                styles.navButtonPrev,
                            )}
                            aria-label="Forrige bilde">
                            <IconChevronLeft16 aria-hidden />
                        </button>
                    )}
                    {showNextButton && (
                        <button
                            type="button"
                            onClick={handleNavigationClickNext}
                            className={cn(
                                'flex items-center justify-center absolute border-none rounded-full z-10 md:opacity-0 pl-2',
                                styles.navButton,
                                styles.navButtonNext,
                            )}
                            aria-label="Neste bilde">
                            <IconChevronRight16 aria-hidden />
                        </button>
                    )}
                    <div
                        className={cn('absolute bottom-8 left-1/2 z-10 overflow-hidden', styles.indicators, {
                            [styles.indicatorsFew]: totalImages <= 5,
                        })}>
                        <div
                            className={cn('flex items-center', styles.dotsTrack)}
                            style={{ transform: `translateX(${dotsTrackOffsetRem}rem)` }}
                            data-testid="dots-track">
                            {displayableImages.map(({ originalIndex }, index) => (
                                <span
                                    key={originalIndex}
                                    data-testid={`carousel-dot-${index}`}
                                    className={cn(styles.dot, {
                                        [styles.dotTiny]: edgeDotIndices.has(index),
                                        [styles.dotActive]: index === activeImageIndex,
                                    })}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
});

ImageCarousel.displayName = 'ImageCarousel';
